import { useCallback } from 'react';
import { useNavigate } from '@remix-run/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { toast } from 'sonner';
import { uiContextAtomDesignBoardsActions } from '@rocco/ui/design-board/components/DesignBoardsActions';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { ReactComponent as ArrowRightIcon } from '@rocco/icons/svg/arrow/arrow-right.svg';
import type { PinItemType } from '@modules/design-board/type';
import type { DesignBoardListQuery } from 'generated/graphql';
import type { FollowEventSource } from '@rocco/ui/design-board/components/DesignBoardsActions';
import type { DesignBoardViewModel } from '@rocco/ui/design-board/view-models/design-board';
import { useAPNativeAppDetection } from '../../../../utils/mobile-app-detection';
import {
    uiStateAtomSelectDesignBoardModalIsOpened,
    uiStateAtomProcessingBoardIds,
} from '@rocco/ui/design-board/components/SelectDesignBoardModal';

type Pin =
    DesignBoardListQuery['DesignBoardListItems']['nodes'][number]['Pins'][number];

interface ItemToFollow {
    id: number;
    libraryItemId?: number;
}

/**
 * Atom to store the list of pins (design board items) that the user has saved.
 * This is persisted in local storage.
 */
export const pinsAtom = atomWithStorage<Pin[]>('pins', []);
export const moodPinsAtom = atomWithStorage<number[]>('moodPins', []);
export const moodImagePinsAtom = atomWithStorage<number[]>('moodImagePins', []);

/**
 * Custom hook to handle following items to a design board.
 *
 * @returns An object containing the handleFollow and isInDesignBoard functions.
 */
export const useHandleFollow = () => {
    const designBoardsActions = useAtomValue(uiContextAtomDesignBoardsActions);
    const setSelectDesignBoardModalOpened = useSetAtom(
        uiStateAtomSelectDesignBoardModalIsOpened
    );
    const setProcessingBoardIds = useSetAtom(uiStateAtomProcessingBoardIds);
    const pins = useAtomValue(pinsAtom);
    const moodPins = useAtomValue(moodPinsAtom);
    const moodImagePins = useAtomValue(moodImagePinsAtom);
    const navigate = useNavigate();

    const { isAPNativeApp } = useAPNativeAppDetection();

    /**
     * Checks if an item is already in the design board.
     *
     * @param itemId - The ID of the item to check. For PhotoPins, this should be the LibraryImageID.
     * @param itemType - The type of the item to check. If not provided, assumes
     *   it is a directory item type (professional, project, product, article).
     * @returns True if the item is in the design board, false otherwise.
     */
    const isInDesignBoard = useCallback(
        (itemId: number, itemType?: PinItemType) => {
            const inPins = pins.some((pin) => {
                if (itemType && pin.__typename !== itemType) {
                    return false;
                }
                if (pin.__typename === 'PhotoPin') {
                    return pin.LibraryImageID === itemId;
                } else if (pin.__typename === 'MemberUploadPin') {
                    return pin.TypeID === itemId;
                }
                return pin.PageTypeID === itemId;
            });

            // Check moodPins for page-based items and moodImagePins for PhotoPins
            // This avoids ID collisions between Page IDs and Image IDs
            const inMoodPins =
                itemType === 'PhotoPin'
                    ? (moodImagePins ?? []).includes(itemId)
                    : (moodPins ?? []).includes(itemId);

            return inPins || inMoodPins;
        },
        [pins, moodPins, moodImagePins]
    );

    /**
     * Handles the action of following an item to a design board.
     *
     * @param items - The item(s) to follow. `id` is the ID of the item and `libraryItemId` is the ID of the associated library item (product, project, article).
     * @param itemType - The type of the items to follow.
     * @param source - The source of the follow event.
     * @returns `true` if the items were successfully followed.
     */
    const handleFollow = useCallback(
        async (
            items: ItemToFollow | ItemToFollow[],
            itemType: PinItemType,
            source?: FollowEventSource
        ) => {
            if (isAPNativeApp) {
                // Call the native app handler if available
                if (
                    typeof window !== 'undefined' &&
                    'handleFollowInNativeApp' in window
                ) {
                    const itemsArray = Array.isArray(items) ? items : [items];
                    try {
                        const result = await (
                            window as Window & {
                                handleFollowInNativeApp: (
                                    items: ItemToFollow[],
                                    itemType: PinItemType,
                                    source?: FollowEventSource
                                ) => Promise<boolean>;
                            }
                        ).handleFollowInNativeApp(itemsArray, itemType, source);

                        return result;
                    } catch (error) {
                        console.error(
                            'Error calling native follow handler:',
                            error
                        );
                        return false; // Return false on error to allow fallback
                    }
                }

                return false; // Return false to allow fallback
            } else {
                if (!designBoardsActions) {
                    return false;
                }

                const itemsToFollow = (
                    Array.isArray(items) ? items : [items]
                ).filter((item) => item.id !== 0);

                if (!itemsToFollow.length) {
                    return false;
                }

                const isPhotoPin = itemType === 'PhotoPin';

                // Filter out items that are already in the design board
                const filteredItemsToFollow = isPhotoPin
                    ? itemsToFollow.filter(
                          ({ id }) => !isInDesignBoard(id, itemType)
                      )
                    : itemsToFollow.filter(
                          ({ id, libraryItemId }) =>
                              !isInDesignBoard(libraryItemId ?? id, itemType)
                      );

                filteredItemsToFollow.forEach(({ id, libraryItemId }) => {
                    designBoardsActions.trackingConfig?.onBeforeFollow?.(
                        isPhotoPin ? id : (libraryItemId ?? id),
                        source
                    );
                });

                let results;
                try {
                    results =
                        await designBoardsActions.showSelectDesignBoardModal(
                            true,
                            itemsToFollow.map(({ libraryItemId, id }) =>
                                isPhotoPin ? id : (libraryItemId ?? id)
                            ),
                            itemType
                        );
                } catch (e) {
                    // Modal cancelled
                    return false;
                }

                if (!results || results.length === 0) {
                    return false;
                }

                let anyActionSucceeded = false;
                let addedBoardCount = 0;
                let removedBoardCount = 0;
                let lastAffectedBoard: DesignBoardViewModel | null = null;
                let lastAffectedBoardType: 'boards' | 'moodboards' | null =
                    null;

                for (const result of results) {
                    const { board, boardType, action } = result;

                    if (!board) {
                        continue;
                    }

                    setProcessingBoardIds((ids) => [...ids, board.id]);
                    try {
                        designBoardsActions.trackingConfig?.onAfterBoardSelect?.(
                            board,
                            source
                        );

                        const isMoodBoard = boardType === 'moodboards';

                        if (action === 'add') {
                            const toAddToBoard = itemsToFollow;
                            if (toAddToBoard.length > 0) {
                                const addedCount = isMoodBoard
                                    ? await designBoardsActions.addItemsToMoodBoard(
                                          board.id,
                                          itemType,
                                          toAddToBoard.map(({ id }) => id)
                                      )
                                    : await designBoardsActions.addItemsToBoard(
                                          board.id,
                                          itemType,
                                          toAddToBoard.map(({ id }) => id)
                                      );
                                if (addedCount > 0) {
                                    anyActionSucceeded = true;
                                    addedBoardCount++;
                                    lastAffectedBoard = board;
                                    lastAffectedBoardType = boardType;
                                    filteredItemsToFollow.forEach(
                                        ({ id, libraryItemId }) => {
                                            designBoardsActions.trackingConfig?.onAfterFollow?.(
                                                isPhotoPin
                                                    ? id
                                                    : (libraryItemId ?? id),
                                                source
                                            );
                                        }
                                    );
                                }
                            }
                        } else if (action === 'remove') {
                            const toRemoveFromBoard = itemsToFollow;
                            if (toRemoveFromBoard.length > 0) {
                                const removedItemsCount = isMoodBoard
                                    ? await designBoardsActions.removeItemsFromMoodBoard(
                                          board.id,
                                          toRemoveFromBoard.map(({ id }) => id)
                                      )
                                    : await designBoardsActions.removeItemsFromBoard(
                                          board.id,
                                          toRemoveFromBoard.map(({ id }) => id)
                                      );
                                if (removedItemsCount > 0) {
                                    anyActionSucceeded = true;
                                    removedBoardCount++;
                                    lastAffectedBoard = board;
                                    lastAffectedBoardType = boardType;
                                }
                            }
                        }
                    } finally {
                        setProcessingBoardIds((ids) =>
                            ids.filter((id) => id !== board.id)
                        );
                    }
                }

                // Toast display logic outside the loop
                if (anyActionSucceeded) {
                    setSelectDesignBoardModalOpened(false);
                    if (addedBoardCount === 1 && removedBoardCount === 0) {
                        const isMoodBoard =
                            lastAffectedBoardType === 'moodboards';
                        if (
                            designBoardsActions.displayMode ===
                            DisplayMode.Desktop
                        ) {
                            toast.success(
                                isMoodBoard
                                    ? 'Saved to Mood Board'
                                    : 'Saved to Design Board',
                                {
                                    action: {
                                        label: 'Dismiss',
                                        onClick: () => toast.dismiss(),
                                    },
                                    cancel: {
                                        label: (
                                            <span className="flex items-center justify-between gap-2">
                                                <span>
                                                    {isMoodBoard
                                                        ? 'View Mood Board'
                                                        : 'View Design Board'}
                                                </span>
                                                <ArrowRightIcon className="size-4.5" />
                                            </span>
                                        ),
                                        onClick: () => {
                                            if (
                                                isMoodBoard &&
                                                lastAffectedBoard?.project
                                                    ?.urlSegment &&
                                                lastAffectedBoard?.urlSegment
                                            ) {
                                                navigate(
                                                    `/app/project/${lastAffectedBoard.project.urlSegment}/mood-boards/${lastAffectedBoard.urlSegment}`
                                                );
                                            } else if (
                                                !isMoodBoard &&
                                                lastAffectedBoard?.urlSegment
                                            ) {
                                                navigate(
                                                    `/member/design-boards/${lastAffectedBoard.urlSegment}`
                                                );
                                            }
                                        },
                                    },
                                }
                            );
                        } else {
                            toast.success(
                                isMoodBoard
                                    ? 'Saved to Mood Board'
                                    : 'Saved to Design Board',
                                {
                                    action: {
                                        label: 'View',
                                        onClick: () => {
                                            if (
                                                isMoodBoard &&
                                                lastAffectedBoard?.project
                                                    ?.urlSegment &&
                                                lastAffectedBoard?.urlSegment
                                            ) {
                                                navigate(
                                                    `/app/project/${lastAffectedBoard.project.urlSegment}/design-boards/${lastAffectedBoard.urlSegment}`
                                                );
                                            } else if (
                                                !isMoodBoard &&
                                                lastAffectedBoard?.urlSegment
                                            ) {
                                                navigate(
                                                    `/member/design-boards/${lastAffectedBoard.urlSegment}`
                                                );
                                            }
                                        },
                                    },
                                }
                            );
                        }
                    } else if (
                        removedBoardCount === 1 &&
                        addedBoardCount === 0
                    ) {
                        const isMoodBoard =
                            lastAffectedBoardType === 'moodboards';
                        toast.success(
                            isMoodBoard
                                ? 'Removed from Mood Board'
                                : 'Removed from Design Board',
                            {
                                action: {
                                    label: 'Dismiss',
                                    onClick: () => toast.dismiss(),
                                },
                            }
                        );
                    } else if (addedBoardCount > 0 && removedBoardCount > 0) {
                        toast.success(
                            `Added to ${addedBoardCount} board${
                                addedBoardCount > 1 ? 's' : ''
                            } and removed from ${removedBoardCount} board${
                                removedBoardCount > 1 ? 's' : ''
                            }.`,
                            {
                                action: {
                                    label: 'Dismiss',
                                    onClick: () => toast.dismiss(),
                                },
                            }
                        );
                    } else if (addedBoardCount > 1 && removedBoardCount === 0) {
                        toast.success(
                            `Added to ${addedBoardCount} board${
                                addedBoardCount > 1 ? 's' : ''
                            }.`,
                            {
                                action: {
                                    label: 'Dismiss',
                                    onClick: () => toast.dismiss(),
                                },
                            }
                        );
                    } else if (removedBoardCount > 1 && addedBoardCount === 0) {
                        toast.success(
                            `Removed from ${removedBoardCount} board${
                                removedBoardCount > 1 ? 's' : ''
                            }.`,
                            {
                                action: {
                                    label: 'Dismiss',
                                    onClick: () => toast.dismiss(),
                                },
                            }
                        );
                    }
                }

                return anyActionSucceeded;
            }
        },
        [
            designBoardsActions,
            navigate,
            isInDesignBoard,
            setSelectDesignBoardModalOpened,
            setProcessingBoardIds,
            isAPNativeApp,
        ]
    );

    return {
        handleFollow,
        isInDesignBoard,
    };
};
